/**
 * SimplifiedUnderwayPhase - Streamlined version with quick entry buttons
 */

import React, { useState } from 'react';
import { 
  Anchor, CheckCircle, Plus, Users, Eye, Wrench
} from 'lucide-react';
import UnderwayModals from './UnderwayModals';
import { getQuickActions } from './underwayQuickActions';

const SimplifiedUnderwayPhase = ({ 
  activeVoyage,
  logEntries = [],
  onUpdateVoyage,
  onAllFast,
  onAddLogEntry,
  crew = [],
  currentVessel,
  vesselType = 'coastal' // 'inland', 'coastal', 'commercial'
}) => {
  const [customEntry, setCustomEntry] = useState('');
  const [hasLetGo, setHasLetGo] = useState(activeVoyage?.status === 'active');
  const [activeModal, setActiveModal] = useState(null);
  
  // Modal form states
  const [positionData, setPositionData] = useState({ lat: '', lon: '', source: 'manual' });
  const [weatherData, setWeatherData] = useState({ 
    windForce: '', 
    windDir: '', 
    seaState: '', 
    visibility: '', 
    barometer: '',
    source: 'manual' 
  });
  const [radioData, setRadioData] = useState({ 
    channel: '', 
    from: '', 
    to: '', 
    message: '', 
    type: 'routine' 
  });
  const [courseData, setCourseData] = useState({ course: '', speed: '' });
  const [incidentData, setIncidentData] = useState({ 
    type: 'near_miss', 
    brief: '', 
    severity: 'low',
    actionTaken: '' 
  });

  // Handle Let Go (actual departure)
  const handleLetGo = () => {
    // Log the let go event
    onAddLogEntry({
      type: 'LET_GO',
      category: 'navigation',
      summary: 'Let Go - All Lines',
      description: `Departed ${activeVoyage?.departurePort}`,
      voyageId: activeVoyage?.id,
      timestamp: new Date().toISOString()
    });

    // Update voyage status to active
    if (onUpdateVoyage && activeVoyage) {
      onUpdateVoyage({
        ...activeVoyage,
        status: 'active',
        actualDepartureTime: new Date().toISOString()
      });
    }

    setHasLetGo(true);
  };

  // Get GPS position
  const getGPSPosition = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setPositionData({
            lat: position.coords.latitude.toFixed(4),
            lon: position.coords.longitude.toFixed(4),
            source: 'gps'
          });
        },
        (error) => {

          alert('Could not get GPS position. Please enter manually.');
        }
      );
    } else {
      alert('GPS not available on this device');
    }
  };

  // Log position with proper data
  const logPosition = () => {
    if (!positionData.lat || !positionData.lon) return;
    
    onAddLogEntry({
      type: 'POSITION',
      category: 'navigation',
      summary: `Position: ${positionData.lat}°, ${positionData.lon}°`,
      description: `Position update (${positionData.source === 'gps' ? 'GPS' : 'Manual'})`,
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id,
      position: { 
        lat: positionData.lat, 
        lon: positionData.lon,
        source: positionData.source 
      }
    });
    
    setPositionData({ lat: '', lon: '', source: 'manual' });
    setActiveModal(null);
  };

  // Log weather with detailed data
  const logWeather = () => {
    if (!weatherData.windForce) return;
    
    const summary = `Weather: Force ${weatherData.windForce} from ${weatherData.windDir || 'N/A'}`;
    const details = [];
    if (weatherData.seaState) details.push(`Sea State ${weatherData.seaState}`);
    if (weatherData.visibility) details.push(`Vis: ${weatherData.visibility}`);
    if (weatherData.barometer) details.push(`${weatherData.barometer} mb`);
    
    onAddLogEntry({
      type: 'WEATHER',
      category: 'weather',
      summary,
      description: details.join(', ') || 'Weather observation',
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id,
      data: weatherData
    });
    
    setWeatherData({ windForce: '', windDir: '', seaState: '', visibility: '', barometer: '', source: 'manual' });
    setActiveModal(null);
  };
  
  // Log radio traffic
  const logRadio = () => {
    if (!radioData.message) return;
    
    const prefix = radioData.type === 'distress' ? '🆘 DISTRESS: ' : '';
    const summary = `${prefix}Radio Traffic CH${radioData.channel || '16'}`;
    
    onAddLogEntry({
      type: radioData.type === 'distress' ? 'DISTRESS_RADIO' : 'RADIO_TRAFFIC',
      category: 'communication',
      summary,
      description: `From: ${radioData.from || 'Unknown'} To: ${radioData.to || 'All Stations'} - ${radioData.message}`,
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id,
      priority: radioData.type === 'distress' ? 'high' : 'normal',
      data: radioData
    });
    
    setRadioData({ channel: '', from: '', to: '', message: '', type: 'routine' });
    setActiveModal(null);
  };
  
  // Create basic incident report
  const createIncidentReport = () => {
    if (!incidentData.brief) return;
    
    const summary = `${incidentData.type === 'near_miss' ? 'Near Miss' : 
                     incidentData.type === 'defect' ? 'Defect Reported' : 
                     incidentData.type === 'injury' ? 'Injury/Illness' : 'Incident'}`;
    
    onAddLogEntry({
      type: 'INCIDENT',
      category: 'safety',
      summary,
      description: `${incidentData.brief}. Action: ${incidentData.actionTaken || 'To be determined'}`,
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id,
      priority: incidentData.severity === 'high' ? 'high' : 'normal',
      data: {
        ...incidentData,
        requiresFullReport: true,
        reportId: `INC-${Date.now()}`
      }
    });
    
    alert('Basic incident logged. Remember to complete full incident report.');
    setIncidentData({ type: 'near_miss', brief: '', severity: 'low', actionTaken: '' });
    setActiveModal(null);
  };

  // Get quick actions based on vessel type
  const availableActions = getQuickActions(vesselType, setActiveModal, setRadioData, radioData).map(action => {
    // Handle special actions that need direct logging
    const originalAction = action.action;
    return {
      ...action,
      action: () => {
        const result = originalAction();
        if (result) {
          if (result.type === 'WATCH_CHANGE') {
            onAddLogEntry({
              type: 'WATCH_CHANGE',
              category: 'crew',
              summary: `Watch change: ${result.officer} has the watch`,
              description: '',
              timestamp: new Date().toISOString(),
              voyageId: activeVoyage?.id
            });
          } else if (result.type === 'SIGHTING') {
            onAddLogEntry({
              type: 'SIGHTING',
              category: 'navigation',
              summary: 'Traffic/object sighted',
              description: result.sighting,
              timestamp: new Date().toISOString(),
              voyageId: activeVoyage?.id
            });
          } else if (result.type === 'TECHNICAL') {
            onAddLogEntry({
              type: 'TECHNICAL',
              category: 'machinery',
              summary: 'Technical log',
              description: result.issue,
              timestamp: new Date().toISOString(),
              voyageId: activeVoyage?.id
            });
          }
        }
      }
    };
  });

  // Custom entry handler
  const handleCustomEntry = () => {
    if (!customEntry.trim()) return;
    
    onAddLogEntry({
      type: 'GENERAL',
      category: 'general',
      summary: customEntry,
      description: '',
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id
    });
    setCustomEntry('');
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Underway</h3>
          <p className="text-sm text-gray-500 mt-1">
            {hasLetGo ? 'Voyage in progress - log events as they occur' : 'Ready to depart - click "Let Go" to begin voyage'}
          </p>
        </div>
        
        <div className="p-5 space-y-4">
          {/* Let Go Button - only show if not already departed */}
          {!hasLetGo && (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Ready to Depart</h4>
              <p className="text-sm text-green-700 mb-4">
                Pre-departure checks complete. Click "Let Go" to officially begin the voyage.
              </p>
              <button
                onClick={handleLetGo}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2"
              >
                <Anchor className="h-5 w-5" />
                Let Go - Begin Voyage
              </button>
            </div>
          )}

          {/* Quick Actions Grid */}
          {hasLetGo && (
            <>
              {/* Essential log entries reminder */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Essential Log Entries</h4>
                <div className="grid grid-cols-1 gap-1 text-xs text-blue-700">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    <span>Position - Every 4 hours at sea</span>
                  </div>
                  {vesselType !== 'inland' && (
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      <span>Weather - Every watch</span>
                    </div>
                  )}
                  {vesselType === 'commercial' && (
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      <span>Watch changes - As they occur</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    <span>Any incidents, traffic, or course changes</span>
                  </div>
                </div>
              </div>

              {/* Quick action buttons */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {availableActions.map(action => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.label}
                      onClick={action.action}
                      className="p-3 rounded-lg border-2 border-gray-200 bg-white hover:bg-gray-50 hover:shadow-md transition-all"
                    >
                      <Icon className="h-6 w-6 mx-auto mb-1 text-gray-600" />
                      <p className="text-sm font-medium text-gray-900">{action.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{action.description}</p>
                    </button>
                  );
                })}
              </div>

              {/* Custom entry */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Log Entry
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customEntry}
                    onChange={(e) => setCustomEntry(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCustomEntry()}
                    placeholder="Type any other log entry..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={handleCustomEntry}
                    disabled={!customEntry.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Quick templates */}
                <div className="mt-2 flex flex-wrap gap-1">
                  <button
                    onClick={() => setCustomEntry('Passing buoy')}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Passing buoy
                  </button>
                  <button
                    onClick={() => setCustomEntry('Traffic sighted')}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Traffic sighted
                  </button>
                  <button
                    onClick={() => setCustomEntry('Speed reduced')}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Speed reduced
                  </button>
                  <button
                    onClick={() => setCustomEntry('Entering channel')}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Entering channel
                  </button>
                </div>
              </div>

              {/* All Fast Button */}
              <div className="mt-6 pt-6 border-t flex justify-center">
                <button
                  onClick={onAllFast}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 flex items-center gap-2"
                >
                  <Anchor className="h-5 w-5" />
                  All Fast - Arrival
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Modal Dialogs */}
      <UnderwayModals 
        activeModal={activeModal}
        setActiveModal={setActiveModal}
        positionData={positionData}
        setPositionData={setPositionData}
        weatherData={weatherData}
        setWeatherData={setWeatherData}
        radioData={radioData}
        setRadioData={setRadioData}
        courseData={courseData}
        setCourseData={setCourseData}
        incidentData={incidentData}
        setIncidentData={setIncidentData}
        getGPSPosition={getGPSPosition}
        logPosition={logPosition}
        logWeather={logWeather}
        logRadio={logRadio}
        createIncidentReport={createIncidentReport}
        onAddLogEntry={onAddLogEntry}
        activeVoyage={activeVoyage}
      />
    </div>
  );
};

export default SimplifiedUnderwayPhase;